import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCopy, IconPlus, IconTrash } from '@tabler/icons-react'
import type { SeatingConfiguration } from '@/lib/types'
import styles from './SeatingChart.module.css'

interface SeatingConfigurationBarProps {
  configurations: SeatingConfiguration[]
  activeConfigurationId: string | null
  activeConfigurationName: string
  configBusy: boolean
  hasUnsavedChanges: boolean
  onSelectConfiguration: (id: string) => Promise<void>
  onCreateConfiguration: (name: string, duplicateFromId?: string) => Promise<boolean>
  onUpdateConfigurationName: (name: string) => Promise<boolean>
  onDeleteConfiguration: (id: string) => Promise<boolean>
}

export function SeatingConfigurationBar({
  configurations,
  activeConfigurationId,
  activeConfigurationName,
  configBusy,
  hasUnsavedChanges,
  onSelectConfiguration,
  onCreateConfiguration,
  onUpdateConfigurationName,
  onDeleteConfiguration,
}: SeatingConfigurationBarProps) {
  const [configName, setConfigName] = useState(activeConfigurationName)
  const [newModalOpen, setNewModalOpen] = useState(false)
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [switchModalOpen, setSwitchModalOpen] = useState(false)
  const [pendingConfigId, setPendingConfigId] = useState<string | null>(null)
  const [modalName, setModalName] = useState('')

  useEffect(() => {
    setConfigName(activeConfigurationName)
  }, [activeConfigurationName, activeConfigurationId])

  const switchToConfiguration = useCallback(
    async (id: string) => {
      await onSelectConfiguration(id)
      setSwitchModalOpen(false)
      setPendingConfigId(null)
    },
    [onSelectConfiguration]
  )

  const handleSelectChange = (value: string | null) => {
    if (!value || value === activeConfigurationId) return
    if (hasUnsavedChanges) {
      setPendingConfigId(value)
      setSwitchModalOpen(true)
      return
    }
    void switchToConfiguration(value)
  }

  const handleNameBlur = async () => {
    const trimmed = configName.trim()
    if (!trimmed || trimmed === activeConfigurationName) {
      setConfigName(activeConfigurationName)
      return
    }
    const success = await onUpdateConfigurationName(trimmed)
    if (!success) {
      setConfigName(activeConfigurationName)
    }
  }

  const handleCreate = async () => {
    const trimmed = modalName.trim()
    if (!trimmed) return
    const success = await onCreateConfiguration(trimmed)
    if (success) {
      setNewModalOpen(false)
      setModalName('')
      notifications.show({
        title: 'Konfiguration erstellt',
        message: `"${trimmed}" wurde angelegt.`,
        color: 'green',
      })
    }
  }

  const handleDuplicate = async () => {
    const trimmed = modalName.trim()
    if (!trimmed || !activeConfigurationId) return
    const success = await onCreateConfiguration(trimmed, activeConfigurationId)
    if (success) {
      setDuplicateModalOpen(false)
      setModalName('')
      notifications.show({
        title: 'Konfiguration dupliziert',
        message: `"${trimmed}" wurde erstellt.`,
        color: 'green',
      })
    }
  }

  const handleDelete = async () => {
    if (!activeConfigurationId) return
    const success = await onDeleteConfiguration(activeConfigurationId)
    if (success) {
      setDeleteModalOpen(false)
      notifications.show({
        title: 'Konfiguration gelöscht',
        message: `"${activeConfigurationName}" wurde entfernt.`,
        color: 'green',
      })
    }
  }

  const openDuplicateModal = () => {
    setModalName(activeConfigurationName ? `${activeConfigurationName} (Kopie)` : 'Kopie')
    setDuplicateModalOpen(true)
  }

  return (
    <>
      <Group gap="sm" wrap="wrap" align="flex-end" className={styles.configBar}>
        <Select
          label="Konfiguration"
          value={activeConfigurationId}
          onChange={handleSelectChange}
          data={configurations.map((c) => ({ value: c.id, label: c.name }))}
          disabled={configBusy || configurations.length === 0}
          style={{ minWidth: 180 }}
          size="sm"
        />
        <TextInput
          label="Name"
          value={configName}
          onChange={(e) => setConfigName(e.currentTarget.value)}
          onBlur={() => void handleNameBlur()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
          }}
          disabled={configBusy || !activeConfigurationId}
          style={{ minWidth: 200, flex: 1 }}
          size="sm"
        />
        <Group gap="xs" pb={1}>
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => {
              setModalName('')
              setNewModalOpen(true)
            }}
            disabled={configBusy}
          >
            Neu
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            leftSection={<IconCopy size={14} />}
            onClick={openDuplicateModal}
            disabled={configBusy || !activeConfigurationId}
          >
            Duplizieren
          </Button>
          <Button
            size="compact-sm"
            variant="light"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={() => setDeleteModalOpen(true)}
            disabled={configBusy || !activeConfigurationId || configurations.length <= 1}
          >
            Löschen
          </Button>
        </Group>
      </Group>

      <Modal
        opened={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        title="Neue Konfiguration"
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void handleCreate()
          }}
        >
          <Stack>
            <TextInput
              label="Name"
              placeholder="z.B. Plan B"
              value={modalName}
              onChange={(e) => setModalName(e.currentTarget.value)}
              required
              autoFocus
            />
            <Group justify="flex-end">
              <Button variant="default" type="button" onClick={() => setNewModalOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={configBusy} disabled={!modalName.trim()}>
                Erstellen
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        title="Konfiguration duplizieren"
        centered
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void handleDuplicate()
          }}
        >
          <Stack>
            <Text size="sm" c="dimmed">
              Kopiert Tische und Zuweisungen der aktuellen Konfiguration.
            </Text>
            <TextInput
              label="Name"
              value={modalName}
              onChange={(e) => setModalName(e.currentTarget.value)}
              required
              autoFocus
            />
            <Group justify="flex-end">
              <Button variant="default" type="button" onClick={() => setDuplicateModalOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={configBusy} disabled={!modalName.trim()}>
                Duplizieren
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Konfiguration löschen"
        centered
      >
        <Stack>
          <Text size="sm">
            Möchten Sie <strong>{activeConfigurationName}</strong> wirklich löschen? Alle Tische und
            Zuweisungen dieser Konfiguration werden entfernt.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
              Abbrechen
            </Button>
            <Button color="red" loading={configBusy} onClick={() => void handleDelete()}>
              Löschen
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={switchModalOpen}
        onClose={() => {
          setSwitchModalOpen(false)
          setPendingConfigId(null)
        }}
        title="Ungespeicherte Änderungen"
        centered
      >
        <Stack>
          <Text size="sm">
            Es gibt ungespeicherte Änderungen. Beim Wechsel gehen diese verloren.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => {
                setSwitchModalOpen(false)
                setPendingConfigId(null)
              }}
            >
              Abbrechen
            </Button>
            <Button
              color="red"
              onClick={() => pendingConfigId && void switchToConfiguration(pendingConfigId)}
            >
              Wechseln
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
